import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Subject, Observable, throwError } from "rxjs";
import { Pair } from "../obj/Pair";
import { map, catchError } from "rxjs/operators";
import { environment } from "../../environments/environment";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private loggedIn = false;
    private loggedInAdmin = false;
    private loginDataInitiated = false;
    private loginActivitiesSource = new Subject<boolean>();
    loginActivities$ = this.loginActivitiesSource.asObservable();

    private authActivitiesSource = new Subject<string>();
    authActivities$ = this.authActivitiesSource.asObservable();


    constructor(private http: HttpClient) {
    }

    loginActivity(isLogin: boolean) {
        this.loginActivitiesSource.next(isLogin);
    }

    authActivity(email: string) {
        this.authActivitiesSource.next(email);
    }

    login(email: string, password: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("login", email)
            .set("password", password);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "User/Login",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                sessionStorage.clear();
                this.authenticateBaseOnResponse(response);
                return response;
            }
            ));
    }

    authenticateBaseOnResponse(response: any): void {
        this.saveAuthToken((<Pair>response).value, false);
    }

    adminLogin(login: string, password: string): Observable<any> {
        var httpBody = new HttpParams()
            .set("login", login)
            .set("password", password);
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
        return this.http
            .post(
                environment.WEB_API_URL + "Admin/Login",
                httpBody,
                { headers: headers }
            ).pipe(map(response => {
                this.saveAuthToken(response, true);
                return response;
            }
            ));
    }

    logout() {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("admin_token");        
        sessionStorage.clear();
        this.loggedIn = false;
        this.loginActivity(false);
        this.loginDataInitiated = false;
    }

    isLoggedIn(): boolean | null {
        if (!this.loginDataInitiated) {
            this.loggedIn = !!localStorage.getItem("auth_token");
            if (!this.loggedIn) {
                this.loginDataInitiated = true;
                return false;
            }
            return null;
        }
        return this.loggedIn;
    }

    checkSession(): Observable<boolean> {
        let headers = new HttpHeaders()
            .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
            .set("Authorization", "Bearer " + localStorage.getItem("auth_token"));

        return this.http.get(environment.WEB_API_URL + "User/SessionVerify", { headers: headers })
            .pipe(
                map(response => {
                    if (!(<boolean>response)) {
                        this.logout();
                        this.loginDataInitiated = true;
                        this.loggedIn = false;
                        return false;
                    } else {
                        this.loginDataInitiated = true;
                        this.loggedIn = true;
                        return true;
                    }
                })
            );
    }

    isAdminLoggedIn() {
        if (!this.loginDataInitiated) {
            this.loggedInAdmin = !!localStorage.getItem("admin_token");

            if (this.loggedInAdmin) {
                let headers = new HttpHeaders()
                    .set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8")
                    .set("Authorization", "Bearer " + localStorage.getItem("admin_token"));

                this.http.get(environment.WEB_API_URL + "Admin/SessionVerify", { headers: headers })
                    .pipe(
                        catchError((err: HttpErrorResponse) => {
                            if (err.status === 401) {
                                this.logout();
                            }
                            return throwError(() => new Error(err.statusText));
                        })).subscribe();
            }
            this.loginDataInitiated = true;
        }
        return this.loggedInAdmin;
    }

    checkAuth(auth: string): Observable<string> {
        let queryParams = new HttpParams();
        let headers = new HttpHeaders();
        queryParams = queryParams.set("auth", auth);
        return this.http.get<any>(environment.WEB_API_URL + "CheckAuth", { headers: headers, params: queryParams }).pipe(
            map(response => {
                if (response != null && response.email != null) {
                    return response.email;
                }
                return "";
            }
            )
        );
    }

    getBearer() {
        var token: string | null = "";
        if (localStorage.getItem("admin_token") != null) {
            token = localStorage.getItem("admin_token");
        }
        if (localStorage.getItem("auth_token") != null) {
            token = localStorage.getItem("auth_token");
        }
        return "Bearer " + token;
    }

    isAdminToken(): boolean {
        if (localStorage.getItem("admin_token") != null) {
            return true;
        }
        return false;
    }

    saveAuthToken(data: any, isAdmin: boolean) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("admin_token");
        localStorage.setItem((isAdmin) ? "admin_token" : "auth_token", data.access_token);
        if (isAdmin) {
            this.loggedInAdmin = true;
        } else {
            this.loggedIn = true;
        }
        this.loginDataInitiated = true;
        this.loginActivity(true);
    }
}
